import { useEffect, useState } from "react";
import { Undo2 } from "lucide-react";
import { DocumentRow } from "../components/ui/DocumentRow";
import { EmptyState } from "../components/ui/EmptyState";
import { ListCapNotice } from "../components/ui/ListCapNotice";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet, downloadBlob } from "../api/portal-client";
import { usePortalResource } from "../hooks/usePortalResource";
import { formatDay, formatEuro } from "../lib/format";

interface Avoir {
  id: string;
  createdAt: string;
  status: string;
  note: string | null;
  lineCount: number;
  totalHT: number;
  totalTTC: number;
  downloaded: boolean;
}

/** Plafond serveur de `/me/avoirs` (portal-auth.routes.ts). */
const AVOIRS_CAP = 100;

/**
 * Mes avoirs.
 *
 * W7-B — même correction que les factures : « Aucun avoir pour l'instant »
 * s'affichait dès la fin du chargement, panne réseau et 403 compris. Un avoir
 * est de l'argent dû AU client : lui laisser croire qu'il n'en a aucun quand la
 * lecture a échoué n'est pas acceptable. Un montant absent s'écrit « — ».
 */
export function CreditNotesPage() {
  const { data, status, error, reload } = usePortalResource<Avoir[]>(() =>
    apiGet<Avoir[]>("/me/avoirs"),
  );
  const [list, setList] = useState<Avoir[]>([]);

  useEffect(() => {
    if (data) setList(data);
  }, [data]);

  async function handleDownload(a: Avoir) {
    await downloadBlob(`/me/avoirs/${a.id}/pdf`, `avoir-${a.id.slice(-8)}.pdf`);
    setList((prev) => prev.map((x) => (x.id === a.id ? { ...x, downloaded: true } : x)));
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6">
      <PageHeader title="Mes avoirs" subtitle="Les avoirs émis à votre nom par votre fournisseur." />

      <PortalState status={status} label="vos avoirs" error={error} onRetry={reload} />

      {status === "ready" && list.length === 0 && (
        <EmptyState
          icon={Undo2}
          title="Aucun avoir pour l'instant"
          description="Un avoir apparaîtra ici si votre fournisseur en émet un à votre nom."
          action={{ to: "/invoices", label: "Voir mes factures" }}
        />
      )}

      {status === "ready" && list.length > 0 && (
        <>
          <div className="bg-card border border-border rounded-xl2 overflow-hidden">
            {list.map((a) => (
              <DocumentRow
                key={a.id}
                title={`Avoir ${a.id.slice(-8)}`}
                subtitle={formatDay(a.createdAt)}
                amount={formatEuro(a.totalTTC)}
                downloaded={a.downloaded}
                onDownload={() => handleDownload(a)}
              />
            ))}
          </div>
          <ListCapNotice shown={list.length} cap={AVOIRS_CAP} label="avoirs" genre="m" />
        </>
      )}
    </main>
  );
}
